import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Alert,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Food & Beverage',
  'Real Estate',
  'Consulting',
  'Marketing',
  'Manufacturing',
  'Non-profit',
  'Entertainment',
  'Travel',
  'Fitness',
  'Beauty',
  'Other'
];

const BRAND_TONES = [
  'Professional',
  'Friendly',
  'Creative',
  'Authoritative',
  'Playful',
  'Inspirational',
  'Educational',
  'Casual'
];

const ManualInputForm = ({ onSubmit, websiteUrl, initialData = null, submitLabel = 'Continue with Manual Info', cancelLabel = null, onCancel = null }) => {
  const [formData, setFormData] = useState(() => {
    const defaultData = {
      businessName: '',
      description: '',
      industry: '',
      targetAudience: '',
      brandTone: 'Professional',
      products: []
    };
    
    if (initialData) {
      return {
        ...defaultData,
        ...initialData,
        // Ensure products is always an array
        products: Array.isArray(initialData.products) ? initialData.products : []
      };
    }
    
    return defaultData;
  });
  
  const [currentProduct, setCurrentProduct] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      const newFormData = {
        businessName: '',
        description: '',
        industry: '',
        targetAudience: '',
        brandTone: 'Professional',
        ...initialData,
        // Ensure products is always an array
        products: Array.isArray(initialData?.products) ? initialData.products : []
      };
      setFormData(newFormData);
    }
  }, [initialData]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddProduct = () => {
    const currentProducts = Array.isArray(formData?.products) ? formData.products : [];
    if (currentProduct.trim() && !currentProducts.includes(currentProduct.trim())) {
      setFormData(prev => ({
        ...prev,
        products: [...(Array.isArray(prev?.products) ? prev.products : []), currentProduct.trim()]
      }));
      setCurrentProduct('');
    }
  };

  const handleRemoveProduct = (productToRemove) => {
    setFormData(prev => ({
      ...prev,
      products: (Array.isArray(prev.products) ? prev.products : []).filter(product => product !== productToRemove)
    }));
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddProduct();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const newErrors = {};
    
    // Validate required fields
    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData?.description?.trim()) {
      newErrors.description = 'Business description is required';
    }
    
    if (!formData?.industry) {
      newErrors.industry = 'Please select an industry';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit({
      ...formData,
      extracted: false // Flag to indicate this was manually entered
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {!initialData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Manual Business Information Entry
          </Typography>
          <Typography variant="body2">
            We couldn't automatically extract information from your website. Please provide your business details below.
          </Typography>
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Business Information
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Website: {websiteUrl}
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Business Name"
            value={formData?.businessName || ''}
            onChange={handleInputChange('businessName')}
            error={!!errors.businessName}
            helperText={errors.businessName || 'Enter your business or brand name'}
            placeholder="e.g., Solo Social"
            required
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderWidth: 2,
                }
              }
            }}
          />

          <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.industry}>
            <InputLabel>Industry</InputLabel>
            <Select
              value={formData?.industry || ''}
              onChange={handleInputChange('industry')}
              label="Industry"
              required
            >
              {INDUSTRIES.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
            {errors.industry && (
              <FormHelperText>{errors.industry}</FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            label="Target Audience"
            value={formData?.targetAudience || ''}
            onChange={handleInputChange('targetAudience')}
            error={!!errors.targetAudience}
            helperText={errors.targetAudience || 'Who is your ideal customer? (optional)'}
            placeholder="e.g., Small business owners, Marketing professionals"
            sx={{ mb: 3 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Business Description"
            value={formData?.description || ''}
            onChange={handleInputChange('description')}
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description || 'Describe what your business does'}
            placeholder="e.g., We help businesses create and schedule social media content..."
            sx={{ mb: 3 }}
            required
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Brand Tone</InputLabel>
            <Select
              value={formData?.brandTone || 'Professional'}
              onChange={handleInputChange('brandTone')}
              label="Brand Tone"
            >
              {BRAND_TONES.map((tone) => (
                <MenuItem key={tone} value={tone}>
                  {tone}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>How do you want your brand to sound?</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Products/Services (Optional)
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            label="Add Product/Service"
            value={currentProduct}
            onChange={(e) => setCurrentProduct(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Social Media Templates"
            size="small"
          />
          <Button
            variant="outlined"
            onClick={handleAddProduct}
            disabled={!currentProduct.trim()}
          >
            Add
          </Button>
        </Box>

        {formData?.products && Array.isArray(formData.products) && formData.products.length > 0 && (
          <ChipContainer>
            {(formData.products || []).map((product, index) => (
              <Chip
                key={index}
                label={product}
                onDelete={() => handleRemoveProduct(product)}
                color="primary"
                variant="outlined"
              />
            ))}
          </ChipContainer>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ minWidth: 200 }}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            size="large"
            sx={{ minWidth: 120 }}
            onClick={onCancel}
          >
            {cancelLabel || 'Cancel'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ManualInputForm; 